import type { ReactNode } from "react";
import { Button, Card, Tooltip } from "@bfc-software/pallet";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";
import * as dateFns from "date-fns";
import clsx from "clsx";

import type { PlanRow } from "../-data/plans-model";

import { PlanStatusTag, Progress, AutoTags } from "./task-bits";

/**
 * One task plan.
 *
 * The card is where activation lives, because activation is a decision about a
 * whole wave - releasing tasks one at a time is not something the floor does, and
 * a per-row control would invite it. Everything else on the card exists to make
 * that decision: how much work it releases, which routes it serves, and how far
 * the plan already got.
 *
 * All of that comes from the plan listing alone. `TaskPlanActivationState` rolls
 * up the counts, so a collapsed card costs nothing beyond the row it was drawn
 * from - which is what lets the tasks be fetched only on expansion.
 *
 * The card does not fetch them. `tasks` is handed in already-rendered and only
 * mounted while expanded, so the read lives with the route that owns the query
 * and this file stays a drawing of a `PlanRow`.
 */
export function PlanCard({
  row,
  isExpanded,
  onToggle,
  onActivate,
  isPending,
  tasks,
}: {
  row: PlanRow;
  isExpanded: boolean;
  onToggle: () => void;
  onActivate: () => void;
  /** An activation is in flight for this plan; only its buttons disable. */
  isPending: boolean;
  /** The expanded table. Rendered only while open, so its query is too. */
  tasks: ReactNode;
}) {
  const [t] = useCurrentLocalization();
  const { plan } = row;

  /**
   * What the action column says under the button, or in place of it.
   *
   * The note is the part that carries the reason: a plan with no button and no
   * note is indistinguishable from a plan whose button failed to render.
   */
  const note = row.canActivate
    ? t("tasks.plans.note.releases", { count: row.taskCount })
    : row.statusKey === "completed"
      ? t("tasks.plans.note.complete")
      : row.statusKey === "activating"
        ? t("tasks.plans.note.activating")
        : row.isStarted
          ? t("tasks.plans.note.locked")
          : "";

  return (
    <Card
      layout="body-only"
      data-test-role="plan-card"
      data-plan={plan.id}
      body={
        <>
          {/* The row never wraps. The chevron takes its own width; the other
              columns are flex ratios (identity 4, actions 3) with a
              zero basis, so every card of the same width gets the same column
              edges and the cards line up down the list. Each ratio column is
              `min-w-0` so its content cannot widen its share; the text in the
              identity block ends in an
              ellipsis when the card is too narrow (the "Created" line first,
              then the zone tags), so what has to move is visible rather than
              reflowed. Small screens get their own affordances later. */}
          <div className="flex items-center py-16 px-20">
            {/*
          Every card gets a chevron, whatever its activation state (§0 of the
          implementation plan). The planning worker writes a plan's task rows
          once, at plan time, and activation is a later, separate fact about
          them - so a plan nobody has released still has tasks behind it, just
          read from a different endpoint (`GET /{orgId}/tasks` rather than the
          activation listing; see `usePlanTasks` in `use-plans.ts`). The only
          card with nothing to expand onto is one whose own *planning* produced
          no rows, and that already draws as the ordinary empty state inside an
          expanded card rather than as a missing chevron - auditing why
          planning failed is explicitly out of scope here.
        */}
            <div className="flex-none">
              <Button
                kind="icon-only"
                variant="tertiary"
                icon={isExpanded ? "ChevronDown" : "ChevronRight"}
                label={t(
                  isExpanded ? "tasks.plans.collapse" : "tasks.plans.expand",
                )}
                aria-expanded={isExpanded}
                onPress={onToggle}
                data-test-role="plan-toggle"
              />
            </div>

            <div className="flex flex-5 flex-col min-w-0 ml-16">
              <div className="flex items-center">
                <span className="min-w-0 truncate font-mono text-size-14 font-heading text-on-surface">
                  {plan.name}
                </span>
                {plan.loadBuilderDiagramUrl && (
                  <span className="block small-link ml-8">
                    <a
                      href={plan.loadBuilderDiagramUrl}
                      rel="noopener"
                      target="_blank"
                    >
                      {t("tasks.plans.viewLoadBuilder")}
                    </a>
                  </span>
                )}
                <div className="flex-none ml-8">
                  <PlanStatusTag statusKey={row.statusKey} />
                </div>
                {/*
                  Formatted here rather than through the shared `DateValue`, because
                  the date is inside a sentence - "Created 14 Jul 2026" is one string
                  a translator has to be able to reorder, and DateValue renders a
                  block of its own that cannot sit inside one.
                */}
                <span className="min-w-0 ml-8 truncate text-on-surface-muted text-size-12">
                  {/*
                    SR-342 item 4: MM/DD/YY plus the time it was created. The
                    time half is `p` - date-fns' localised time - which is what
                    the drawer's `PP p` already renders beside it, so the two
                    surfaces of this screen say a time the same way. The date
                    half is the literal `MM/dd/yy` the PM asked for rather than
                    a localised token, which is a deliberate en-only choice: it
                    is an unambiguous format only where the reader expects
                    month first.
                  */}
                  {t("tasks.plans.created", {
                    date: dateFns.format(
                      dateFns.parseISO(plan.createdAt),
                      "MM/dd/yy p",
                    ),
                  })}
                </span>
              </div>
              <div className="flex items-center pt-8">
                <span className="flex-none whitespace-nowrap text-on-surface-variant text-size-13 font-strong">
                  {t("tasks.plans.taskCount", { count: row.taskCount })}
                </span>
                {/* A left border on an empty span, stretched to the row's
                    height, rather than a sized box. */}
                <span
                  className="flex-none self-stretch ml-8 border-l-1 border-outline-variant"
                  aria-hidden="true"
                />
                {/* One clipper for both tag groups: whatever does not fit is
                    cut off the end, so the zones (the less important tags) go
                    before the routes. */}
                <div className="flex min-w-0 flex-1 overflow-hidden ml-8">
                  {/*
                      The routes the wave serves, which is the one thing a collapsed card
                      can say about where the work is: it comes off the plan itself, so it
                      needs none of the task reads an expanded card makes.
                    */}
                  <div className="flex-none">
                    <AutoTags labels={plan.routeCodes} />
                  </div>
                  {/*
                      Where the wave's work physically is, in the auto-toned tags the
                      routes use. Guarded rather than left to `AutoTags`, which renders
                      its row unconditionally (`task-bits.tsx:205-215`) - an empty plan
                      is a normal plan (§9.3) and should add nothing to the row.
                    */}
                  {plan.zoneNames.length > 0 ? (
                    <div className="flex-none ml-8">
                      <AutoTags labels={plan.zoneNames} />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>

            {/*
              The meter with its customers under it, then the button, packed to
              the right: how far along the wave is, what it is for, and what to
              do about it. Stacked rather than side by side so a narrow card
              keeps room for both.
            */}
            <div className="flex flex-3 items-center justify-end min-w-0 ml-16">
              <div className="flex min-w-0 flex-col items-end ml-16">
                <div className="flex-none">
                  {/*
                    Tasks done out of tasks released, not picks: the plan listing rolls
                    up task states and nothing else, and a pick total on a collapsed card
                    would have to come from reading every task in the plan.
                  */}
                  <Progress
                    done={row.tasksDone}
                    total={row.tasksTotal}
                    percent={row.percent}
                    wide={true}
                    label={t("tasks.plans.tasksProgress", {
                      done: row.tasksDone,
                      total: row.tasksTotal,
                    })}
                  />
                </div>
                {/*
                  Plan-scoped, so it can be long: a wave spans many stops. The
                  first name and how many there are in all, with every name in
                  the tooltip. The name truncates; the count never does.
                */}
                {plan.customerNames.length > 0 ? (
                  <div className="flex min-w-0 max-w-full items-center pt-4">
                    <Tooltip
                      content={
                        <div>
                          {plan.customerNames.map((name) => (
                            <div key={name}>{name}</div>
                          ))}
                        </div>
                      }
                      trigger="span"
                      display={{ overflow: "ellipsis" }}
                    >
                      <span className="flex min-w-0 items-center text-on-surface-variant text-size-13">
                        <span className="min-w-0 truncate">
                          {plan.customerNames[0]}
                        </span>
                        <span className="flex-none ml-4">
                          {t("tasks.plans.customerCount", {
                            count: plan.customerNames.length,
                          })}
                        </span>
                      </span>
                    </Tooltip>
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <div className="flex flex-none flex-col items-end ml-16">
                {row.canActivate ? (
                  /*
                  SR-342 item 1.1: the gradient fill and heavier button the PM
                  asked for. Both come from matching what every other outbound
                  page already does rather than from anything new - the primary
                  action on Items, Selectors, Zones, Locations and User groups
                  is `avatar-surface` at the default scale (see
                  `items.tsx`'s "Import item catalog" trigger). This card was
                  the only one still drawing a flat `primary` at `inline`
                  scale, which is what the PM was comparing against.

                  `avatar-surface` is misnamed for what it does - it is the
                  app's CTA tone, not avatar chrome, and not one of its call
                  sites is an avatar. Renaming it is a pallet API change and
                  the design team's call, so it is recorded in the SR-342 doc
                  rather than taken here.
                */
                  <Button
                    kind="text-only"
                    variant="avatar-surface"
                    text={t("tasks.plans.activate")}
                    isDisabled={isPending}
                    onPress={onActivate}
                  />
                ) : (
                  <></>
                )}
                {note ? (
                  <span
                    className={clsx(
                      "text-right text-on-surface-variant text-size-11",
                      row.canActivate && "mt-4",
                    )}
                  >
                    {note}
                  </span>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>

          {isExpanded ? (
            <div className="overflow-x-auto border-t border-outline-variant">
              {tasks}
            </div>
          ) : (
            <></>
          )}
        </>
      }
    />
  );
}
